import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { api, type AcessoEstado, type FounderEstado, type FounderTier } from '../../api';
import { t } from '../../i18n/idioma';
import { useIdioma } from '../../i18n/useIdioma';
import { NOME_DO_JOGO } from '../../marca';
import { Check, Marca, Seta } from './icones';
import { fundoCss } from './imagens';
import { redesDe } from './links';
import { Redes, Rodape, Secao, SecoesDoJogo, useLarguraDosFundos, usePrecarregarHeroi, useRolou, useTituloDaAba } from './comum';
import { PreRegistroCard } from './PreRegistroCard';
import { LoginCard, type AbaLogin } from './LoginCard';
import { useSession } from '../../state/SessionContext';
import { Selo } from './founders/desenhos';
import { FounderFaixa, FounderSecao } from './founders/FounderSecao';
import { FounderModal, type Compra } from './founders/FounderModal';
import './landing.css';

const TRILHA = [1, 2, 3, 4] as const;
const PERGUNTAS = [1, 2, 3, 4, 5, 6] as const;
const PERGUNTAS_FUND = [1, 2, 3, 4, 5] as const;
const OURO = '#e6c452';

/**
 * "Pré-registro aberto" (§02.3). O nome da fase não aparece no site (Renato, 25/10/2026): era o antigo nome do shard
 * e fica só no console e nos e-mails do convite.
 */
function textoDoSelo(estado: AcessoEstado): string {
  return estado.inscricoesAbertas ? t('landing.selo.aberto') : t('landing.selo.fechado');
}

/**
 * A vitrine dos Founder Packs (techspec-founders-pack §06.1). Fechada, fora do ar ou sem resposta: nada dos
 * founders aparece na página (o pré-registro segue igual).
 */
function useFounders(): FounderEstado | null {
  const [estado, setEstado] = useState<FounderEstado | null>(null);
  useEffect(() => {
    let vivo = true;
    void api.founderEstado()
      .then(e => { if (vivo) setEstado(e.vendasAbertas ? e : null); })
      .catch(() => { if (vivo) setEstado(null); });
    return () => { vivo = false; };
  }, []);
  return estado;
}

type Cartao = 'pre' | 'login';

/** `#login` e `#convite` abrem o cartão no login; o resto (e `#pre-registro`), no pré-registro. */
function cartaoDoEndereco(): { cartao: Cartao; aba: AbaLogin } {
  const hash = window.location.hash;
  if (hash === '#convite') return { cartao: 'login', aba: 'convite' };
  if (hash === '#login') return { cartao: 'login', aba: 'entrar' };
  return { cartao: 'pre', aba: 'entrar' };
}

/**
 * A página inicial enquanto o jogo está travado numa fase de testes (techspec-landing-fase §06). O cartão do herói é o
 * pré-registro ou, pelo Entrar do topo, o login com Entrar e Convite (§06.7): uma página só, com o jogo e o acesso.
 */
export function LandingFase({ estado, emailInicial = '', confirmar, senhaLink, convite }: {
  estado: AcessoEstado;
  /** Link do acesso manual: o e-mail já vem no login. */
  emailInicial?: string;
  /** Botão do e-mail de confirmação: o login abre no passo do código. */
  confirmar?: { readonly email: string; readonly codigo: string };
  /** Botão do e-mail de "esqueci a senha". */
  senhaLink?: { readonly email: string; readonly codigo: string };
  /** Link do e-mail do convite (`/convite#c=`): o login abre em Convite com o código e o e-mail. */
  convite?: { readonly codigo: string; readonly email: string };
}) {
  const idioma = useIdioma();
  const raiz = useRef<HTMLDivElement>(null);
  const rolou = useRolou(raiz);
  const largura = useLarguraDosFundos();
  const redes = redesDe(estado.redes);
  const founders = useFounders();
  const [compra, setCompra] = useState<Compra | null>(null);
  const fecharCompra = useCallback(() => setCompra(null), []);
  usePrecarregarHeroi(largura);
  useTituloDaAba(t('landing.tituloPagina'));
  // Aviso da sessão (sessão vencida, conta sem acesso à fase): abre no login, onde ele aparece.
  const { error: erroDaSessao } = useSession();
  const veioDeLink = !!emailInicial || !!confirmar || !!senhaLink || !!convite || !!erroDaSessao;
  const [cartao, setCartao] = useState<Cartao>(() => (veioDeLink ? 'login' : cartaoDoEndereco().cartao));
  const [aba, setAba] = useState<AbaLogin>(() => (convite ? 'convite' : cartaoDoEndereco().aba));

  /** O cartão do herói vira o login na aba pedida: rola até ele e põe o cursor no e-mail. */
  const abrirLogin = useCallback((nova: AbaLogin) => {
    setCartao('login');
    setAba(nova);
    window.setTimeout(() => {
      document.getElementById('login')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      document.getElementById(`email-${nova}`)?.focus({ preventScroll: true });
    }, 60);
  }, []);

  // O endereço muda por link da própria página (`#login`, `#convite`, `#pre-registro`): o cartão acompanha.
  useEffect(() => {
    const ler = () => {
      const hash = window.location.hash;
      if (hash === '#login' || hash === '#convite') abrirLogin(hash === '#convite' ? 'convite' : 'entrar');
      else if (hash === '#pre-registro') setCartao('pre');
    };
    window.addEventListener('hashchange', ler);
    return () => window.removeEventListener('hashchange', ler);
  }, [abrirLogin]);

  // Abriu já no login (link de e-mail, `#login`, `#convite`): o cartão na tela.
  useEffect(() => {
    if (cartao === 'login') document.getElementById('login')?.scrollIntoView({ block: 'center' });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // /#pre-registro (links antigos /alpha e /pre-registro): abre no formulário.
  useEffect(() => {
    if (window.location.hash !== '#pre-registro') return;
    document.getElementById('pre-registro')?.scrollIntoView({ block: 'center' });
    document.getElementById('lp-email')?.focus({ preventScroll: true });
  }, []);

  const irAoFormulario = () => {
    setCartao('pre');
    window.setTimeout(() => {
      document.getElementById('pre-registro')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      window.setTimeout(() => document.getElementById('lp-email')?.focus({ preventScroll: true }), 450);
    }, 60);
  };

  const fundos = {
    '--img-heroi': fundoCss('heroi', largura),
    '--img-final': fundoCss('final', largura)
  } as CSSProperties;

  return (
    <div className="lp" ref={raiz} style={fundos} lang={idioma}>
      <header className={rolou ? 'lp-topo is-rolou' : 'lp-topo'}>
        <div className="lp-envolve lp-topo__in">
          <a className="lp-marca" href="#inicio" aria-label={t('landing.inicio')}>
            <Marca vivo />
            <span>{NOME_DO_JOGO}</span>
          </a>
          <nav aria-label={t('landing.secoes')}>
            <a href="#jogo">{t('landing.nav.jogo')}</a>
            <a href="#telas">{t('landing.nav.telas')}</a>
            <a href="#testes">{t('landing.nav.testes')}</a>
            {founders && <a href="#fundadores" className="lp-f-nav"><Selo metal={OURO} />{t('landing.fund.nav')}</a>}
            <a href="#perguntas">{t('landing.nav.perguntas')}</a>
            <a href="/wiki">{t('landing.nav.wiki')}</a>
          </nav>
          {founders && <a className="lp-f-chip" href="#fundadores" aria-label={t('landing.fund.sobre')}><Selo metal={OURO} />{t('landing.fund.nav')}</a>}
          <a className="lp-btn" href="#login" onClick={e => { e.preventDefault(); abrirLogin('entrar'); }}>{t('landing.entrar')}</a>
        </div>
      </header>

      <main id="inicio">
        <section className="lp-heroi" aria-labelledby="lp-titulo">
          <div className="lp-heroi__fundo" role="img" aria-label={t('landing.heroi.fundo')} />
          <div className="lp-envolve lp-heroi__in">
            <div>
              <span className="lp-selo-fase"><i />{textoDoSelo(estado)}</span>
              <h1 id="lp-titulo">{t('landing.heroi.titulo')} <em>{t('landing.heroi.tituloOuro')}</em></h1>
              <p className="lp-heroi__lead">{t('landing.heroi.lead')}</p>
              <ul className="lp-heroi__pontos">
                <li><Check />{t('landing.heroi.ponto1')}</li>
                <li><Check />{t('landing.heroi.ponto2')}</li>
                <li><Check />{t('landing.heroi.ponto3')}</li>
              </ul>
              {founders && <FounderFaixa />}
            </div>
            {cartao === 'login' ? (
              <div className="lp-gump lp-gump--login" id="login">
                <LoginCard modo="convite" aba={aba} onAba={setAba} onPreRegistro={irAoFormulario}
                  codigoInicial={convite?.codigo ?? ''} emailInicial={convite?.email || emailInicial}
                  confirmar={confirmar} senhaLink={senhaLink} />
              </div>
            ) : (
              <div className="lp-gump" id="pre-registro">
                <PreRegistroCard estado={estado} founders={!!founders} onLogin={abrirLogin} />
              </div>
            )}
            {founders && <FounderFaixa celular />}
          </div>
        </section>

        <SecoesDoJogo />

        <Secao id="testes" pedra centro tituloId="lp-t-testes" sobre={t('landing.testes')} titulo={t('landing.testes.titulo')} texto={t('landing.testes.texto')}>
          <ol className="lp-trilha">
            {TRILHA.map(n => (
              <li key={n}>
                <div className="lp-trilha__n">{n}</div>
                <h3>{t(`landing.t${n}.titulo`)}</h3>
                <p>{t(`landing.t${n}.texto`)}</p>
              </li>
            ))}
          </ol>
          <p className="lp-nota-fase"><b>{t('landing.testes.notaForte')}</b> {t('landing.testes.nota')}</p>
        </Secao>

        {founders && (
          <FounderSecao
            estado={founders}
            onComprar={(tier: FounderTier) => setCompra({ tipo: 'comprar', tier })}
            onSubir={() => setCompra({ tipo: 'subir' })}
            irAoFormulario={irAoFormulario}
          />
        )}

        <Secao id="perguntas" centro tituloId="lp-t-perguntas" sobre={t('landing.nav.perguntas')} titulo={t('landing.perguntas.titulo')}>
          <div className="lp-perguntas">
            {PERGUNTAS.map(n => (
              <details key={n}>
                <summary>{t(`landing.q${n}.p`)}</summary>
                <p>{t(`landing.q${n}.r`)}</p>
              </details>
            ))}
            {founders && PERGUNTAS_FUND.map(n => (
              <details key={`f${n}`}>
                <summary>{t(`landing.fund.q${n}.p`)}</summary>
                <p>{t(`landing.fund.q${n}.r`)}</p>
              </details>
            ))}
          </div>
        </Secao>

        <section className="lp-final" aria-labelledby="lp-t-final">
          <div className="lp-envolve">
            <span className="lp-sobre">{t('landing.final.sobre')}</span>
            <h2 id="lp-t-final">{t('landing.final.titulo')}</h2>
            <p>{t('landing.final.texto')}</p>
            <Redes redes={redes} />
            <div className="lp-final__acoes">
              <a className="lp-btn lp-btn--ouro" href="#pre-registro" onClick={e => { e.preventDefault(); irAoFormulario(); }}>
                {t('landing.final.botao')} <Seta />
              </a>
              {founders && (
                <a className="lp-btn lp-btn--fund" href="#fundadores"><Selo metal={OURO} />{t('landing.fund.final')}</a>
              )}
            </div>
          </div>
        </section>
      </main>

      <Rodape idioma={idioma} redes={redes}>
        <a href="#convite" onClick={e => { e.preventDefault(); abrirLogin('convite'); }}>{t('landing.rodape.convite')}</a>
      </Rodape>

      {founders && compra && (
        <FounderModal estado={founders} compra={compra} chaveSite={estado.antiRobo?.chaveSite ?? null} onFechar={fecharCompra} />
      )}
    </div>
  );
}
